import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Exercise } from '@fluent/types';
import type { LessonState } from './lessonReducer.js';
import { diffAgainstAnswer } from './matchAnswer.js';
import { resultDetail } from './useExerciseAnswering.js';
import './ResultDetail.css';

/** The rule to show for a wrong pick, if the exercise carries one. */
export function whyFor(current: Exercise | undefined): string | null {
  if (
    current?.type !== 'cloze' &&
    current?.type !== 'choose_correct' &&
    current?.type !== 'phrase_choice'
  ) {
    return null;
  }
  return current.why_ka ?? null;
}

/** Tile answers carry punctuation as their own tokens ("house", ".") —
 *  glue them back on so the reveal reads like a sentence. */
export function joinTokens(tokens: readonly string[]): string {
  return tokens.reduce(
    (out, tok) => (out === '' ? tok : /^[.,!?;:]+$/u.test(tok) ? out + tok : `${out} ${tok}`),
    '',
  );
}

export interface ResultDetailArgs {
  t: (key: string, opts?: Record<string, unknown>) => string;
  lastVerdict: LessonState['lastVerdict'];
  current: Exercise | undefined;
  /** What the learner typed / built — lets a wrong reveal mark the words they missed. */
  userAnswer: string | null;
}

/**
 * The detail line of the lesson's result bar (null = no line). On a wrong
 * answer it TEACHES: typed and tile answers show the right sentence with the
 * missed words marked. A missed choice exercise has no detail line: its
 * one-line rule (`why_ka`) is the WhyCard above the bar, and WrongTitle
 * gives screen readers the same rule. Everything else falls back to the
 * plain text of resultDetail().
 * Correct-answer details stay plain strings — the bar's countdown re-arms
 * when its detail changes identity.
 */
export function resultDetailNode({
  t,
  lastVerdict,
  current,
  userAnswer,
}: ResultDetailArgs): ReactNode {
  if (lastVerdict === 'wrong' && current) {
    const answer =
      current.type === 'translate_typed' || current.type === 'listen_type'
        ? current.answer
        : current.type === 'translate_tap'
          ? joinTokens(current.answer_tokens)
          : null;
    if (answer !== null) {
      return (
        <>
          {t('lesson.result.correct_answer_label')}{' '}
          <MarkedAnswer answer={answer} userAnswer={userAnswer} />
        </>
      );
    }
  }
  return resultDetail({ t, lastVerdict, current });
}

/**
 * The red bar's title. For a missed choice exercise it also carries the
 * rule, visually hidden: the bar is the polite live region, so screen
 * readers hear it — the card above is aria-hidden. (In the title, not the
 * detail line: an empty detail paragraph left a gap in the bar.)
 */
export function WrongTitle({ text, why }: { text: string; why: string | null }) {
  return (
    <>
      {text}
      {why && (
        <span className="fluent-why-sr" lang="ka">
          {' '}
          {why}
        </span>
      )}
    </>
  );
}

/**
 * The right answer with the words the learner did not write marked. Marks
 * only when the attempt was close enough for the marks to point at the
 * mistake (at least half the words right) — otherwise the whole sentence
 * would light up and say nothing.
 */
function MarkedAnswer({ answer, userAnswer }: { answer: string; userAnswer: string | null }) {
  if (!userAnswer || userAnswer.trim() === '') return <span lang="en">{answer}</span>;
  const diff = diffAgainstAnswer(userAnswer, answer);
  const missing = diff.filter((d) => d.missing).length;
  if (missing === 0 || missing * 2 > diff.length) return <span lang="en">{answer}</span>;
  return (
    <span lang="en">
      {diff.map((d, i) => (
        <span key={i}>
          {i > 0 && ' '}
          {d.missing ? <mark className="fluent-answer-mark">{d.text}</mark> : d.text}
        </span>
      ))}
    </span>
  );
}

/**
 * The rule as a tip card under the exercise — the same 💡 family as the
 * lesson's grammar-tip card. Tornike left the pick to me (2026-10-08); the
 * card won over a line inside the red bar on contrast: dark on sand reads
 * 10.6:1 (dark mode 12.5:1), white on the bar's lighter red only 3.4:1 —
 * under the 4.5:1 minimum. Scrolls itself into view: on a short phone the
 * result bar would otherwise cover it. aria-hidden: the bar already
 * announces the same rule (resultDetailNode).
 */
export function WhyCard({ text }: { text: string }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // After the result bar has slid in (250ms) and the page has grown the
    // room under it (LessonPlayer measures the dock) — earlier, there is
    // nothing to scroll into yet.
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const id = window.setTimeout(() => {
      ref.current?.scrollIntoView?.({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
    }, 320);
    return () => window.clearTimeout(id);
  }, [text]);
  return (
    <div ref={ref} className="fluent-why-card" data-testid="why-card" aria-hidden="true">
      <p className="fluent-why-card__title">
        <span aria-hidden="true">💡</span> {t('lesson.result.why_label')}
      </p>
      <p className="fluent-why-card__body" lang="ka">
        {text}
      </p>
    </div>
  );
}
